import React, { useEffect, useState } from "react";
import {
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Chip,
  Tooltip,
} from "@mui/material";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../../firebase";

export default function AuditTrailTable({
  accountantUid,
  selectedMonth,
  selectedYear,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAuditData = async () => {
      setLoading(true);
      try {
        const clientSnap = await getDocs(
          query(
            collection(db, "linkedClients"),
            where("accountantUid", "==", accountantUid)
          )
        );
        const clientUids = clientSnap.docs
          .map((doc) => doc.data().clientUid)
          .filter(Boolean);

        const receiptSnap = await getDocs(
          query(
            collection(db, "receipts"),
            where("receiptMonth", "==", selectedMonth),
            where("receiptYear", "==", selectedYear)
          )
        );

        const filteredReceipts = receiptSnap.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((r) => clientUids.includes(r.uid));

        const userDocs = await Promise.all(
          [...new Set(filteredReceipts.map((r) => r.uid))].map((uid) =>
            getDoc(doc(db, "users", uid))
          )
        );
        const userMap = Object.fromEntries(
          userDocs.map((doc) => [
            doc.id,
            doc.exists() ? doc.data().email : "Unknown",
          ])
        );

        const projectIds = [
          ...new Set(filteredReceipts.map((r) => r.projectId).filter(Boolean)),
        ];
        const projectDocs = await Promise.all(
          projectIds.map((pid) => getDoc(doc(db, "projects", pid)))
        );
        const projectMap = Object.fromEntries(
          projectDocs.map((doc) => [
            doc.id,
            doc.exists() ? doc.data().name : "—",
          ])
        );

        setRows(
          filteredReceipts.map((r) => ({
            ...r,
            userEmail: userMap[r.uid] || "—",
            projectName: projectMap[r.projectId] || "—",
          }))
        );
      } catch (err) {
        console.error("AuditTrail error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAuditData();
  }, [accountantUid, selectedMonth, selectedYear]);

  if (loading) return <CircularProgress />;

  if (rows.length === 0) return null;

  return (
    <Paper sx={{ p: 3, mt: 4 }}>
      <Typography variant="h6" gutterBottom>
        🕵️ Audit Trail — Uploads This Month
      </Typography>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Client Email</TableCell>
            <TableCell>Uploaded At</TableCell>
            <TableCell>Total (€)</TableCell>
            <TableCell>VAT (€)</TableCell>
            <TableCell>Project</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id}>
              <TableCell>{r.userEmail}</TableCell>
              <TableCell>
                {r.uploadedAt?.seconds
                  ? new Date(r.uploadedAt.seconds * 1000).toLocaleString()
                  : "—"}
              </TableCell>
              <TableCell>{r.amount?.toFixed(2) ?? "—"}</TableCell>
              <TableCell>{r.vat?.toFixed(2) ?? "—"}</TableCell>
              <TableCell>{r.projectName}</TableCell>
              <TableCell>
                <Chip
                  label={r.status || "Uploaded"}
                  size="small"
                  sx={{
                    backgroundColor:
                      r.status === "Pushed to BrightBooks"
                        ? "#d1fae5"
                        : r.status === "Pending"
                        ? "#fff9c4"
                        : "#e0f2fe",
                    fontWeight: 500,
                  }}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}
